import { z } from "zod";
import {
  DomainModelMetaExtendedSchema,
  DomainModelMetaSchema,
  DomainModelSchema,
} from "../util/index.ts";
import {
  AttributeDataType,
  AttributeDateSchema,
  AttributeSchema,
  ResourceLookup,
} from "./AttributeSchemas.ts";

export const TemplatedReportStatus = {
  ACTIVE: "active",
  ARCHIVED: "archived",
} as const;
export type TemplatedReportStatusType =
  (typeof TemplatedReportStatus)[keyof typeof TemplatedReportStatus];

// IMPROVE: make this a discriminated union when Zod supports nested discriminated unions better (see AttributeSchemas.ts)
export const TemplatedReportInputSchema = z
  .object({
    display_name: z.string(),
    order: z.number().default(0),
    lookup_entity_type: z
      .enum([
        ResourceLookup.ASSET_GROUP,
        ResourceLookup.DEVICE,
        ResourceLookup.EQUIPMENT,
        ResourceLookup.FACILITY,
        ResourceLookup.FLOW,
        ResourceLookup.FORM_SUBMISSION,
      ])
      .optional(),
    lookup_entity_attribute: z.string().optional(),
    lookup_entity_attribute_data_type: z
      .enum([
        AttributeDataType.STRING,
        AttributeDataType.NUMBER,
        AttributeDataType.INTEGER,
        AttributeDataType.BOOLEAN,
        AttributeDataType.DATE,
        AttributeDataType.DATE_TIME,
      ])
      .optional(),
    date_resolution: AttributeDateSchema.shape.date_resolution.optional(),
  })
  .merge(
    AttributeSchema.pick({
      data_type: true,
      description: true,
      pick_list_values: true,
      is_required: true,
    })
  );
export type TemplatedReportInputType = z.infer<
  typeof TemplatedReportInputSchema
>;

export const TemplatedReportSchema = z
  .object({
    name: z.string(),
    name_format: z
      .string()
      .nullable()
      .describe(
        "When creating a report, if this is set, the name will be generated using this specified format instead, and the name field will become optional."
      ),
    display_name: z.string(),
    status: z.enum([
      TemplatedReportStatus.ACTIVE,
      TemplatedReportStatus.ARCHIVED,
    ]),
    input_schema: z.record(z.string(), TemplatedReportInputSchema),
    is_embedded: z.boolean().default(false),
    description: z.string().max(256),
    row_styling_headers: z.record(
      z
        .string()
        .min(3)
        .max(64)
        .regex(/^[A-Za-z0-9_]+$/)
    ),
    deduplication_columns: z.record(
      z
        .array(
          z
            .string()
            .min(3)
            .max(64)
            .regex(/^[A-Za-z0-9_]+$/)
        )
        .max(20)
    ),
  })
  .merge(DomainModelMetaSchema);
export type TemplatedReportType = z.infer<typeof TemplatedReportSchema>;
export type TemplatedReportFilterType = Partial<TemplatedReportType>;

export const UpdateOneTemplatedReportSchema = z.object({
  category_id: z.string().uuid(),
});
export type UpdateOneTemplatedReportType = z.infer<
  typeof UpdateOneTemplatedReportSchema
>;

export const TemplatedReportCategorySchema = z
  .object({
    name: z
      .string()
      .min(3)
      .max(64)
      .regex(/^[a-zA-Z0-9 \-_]*$/),
    company_id: z.string().uuid(),
    description: z.string().max(256),
  })
  .merge(DomainModelSchema)
  .merge(DomainModelMetaSchema)
  .merge(DomainModelMetaExtendedSchema);
export type TemplatedReportCategoryType = z.infer<
  typeof TemplatedReportCategorySchema
>;

export const CreateOneTemplatedReportCategorySchema = z
  .object({})
  .merge(
    TemplatedReportCategorySchema.pick({
      name: true,
    }).required()
  )
  .merge(
    TemplatedReportCategorySchema.pick({
      description: true,
    }).partial()
  );
export type CreateOneTemplatedReportCategoryType = z.infer<
  typeof CreateOneTemplatedReportCategorySchema
>;

export const UpdateOneTemplatedReportCategorySchema =
  TemplatedReportCategorySchema.pick({
    name: true,
    description: true,
  }).partial();
export type UpdateOneTemplatedReportCategoryType = z.infer<
  typeof UpdateOneTemplatedReportCategorySchema
>;

export const TemplatedReportWithCategorySchema = TemplatedReportSchema.merge(
  z.object({
    category: TemplatedReportCategorySchema.pick({
      id: true,
      name: true,
    }).nullable(),
  })
);
export type TemplatedReportWithCategoryType = z.infer<
  typeof TemplatedReportWithCategorySchema
>;
